import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "短片分类", to: "/category/shortfilms" },
  { label: "影视资讯", to: "/category/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40">
      <div className="hidden md:block bg-foreground-950 text-background-300">
        <div className="container-page flex items-center justify-between h-9 text-xs">
          <p className="tracking-wide">
            <span className="text-accent-400 font-semibold">今日更新</span>
            <span className="mx-2 text-background-600">/</span>
            2 部新短片 · 3 条影视资讯
          </p>
          <div className="flex items-center gap-5">
            <Link to="/about" className="cursor-pointer hover:text-background-50 transition-colors">
              关于小电影
            </Link>
            <a
              href="mailto:hello@xiaodianying.studio"
              className="cursor-pointer hover:text-background-50 transition-colors"
            >
              合作联系
            </a>
          </div>
        </div>
      </div>

      <div className="bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="container-page flex items-center gap-3 md:gap-6 h-16 md:h-[74px]">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
            <span className="w-10 h-10 rounded-md bg-primary-500 flex items-center justify-center shrink-0">
              <i className="ri-play-fill text-xl text-background-50" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
                小电影<span className="text-accent-500">.</span>
              </span>
              <span className="hidden sm:block mt-1 text-[10px] tracking-[0.2em] text-foreground-500 uppercase">
                Short Film & News
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 md:ml-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={handleSubmit} className="hidden md:flex items-center ml-auto">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索短片 / 资讯"
                aria-label="站内搜索"
                className="w-32 lg:w-40 xl:w-56 h-10 pl-9 pr-3 text-sm rounded-md border border-background-200 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="ml-2 h-10 px-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="flex items-center gap-1.5 ml-auto md:ml-2">
            <Link
              to="/search"
              aria-label="打开搜索页"
              className="md:hidden w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-lg" />
            </Link>
            <button
              type="button"
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((prev) => !prev)}
              className="md:hidden w-10 h-10 rounded-md flex items-center justify-center text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 animate-fade-in">
          <div className="container-page py-4">
            <form onSubmit={handleSubmit} className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  placeholder="搜索短片 / 资讯"
                  aria-label="站内搜索"
                  className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-background-200 bg-background-100 focus:outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                      isActive
                        ? "text-primary-600 bg-primary-50"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}